const emptyReasons = {
    no_measurement_before_time: '指定的時間以前沒有量測資料。請選擇較晚的時間，或回到最新資料。',
    measurement_not_found: '指定的量測批次已不存在。請回到最新資料再選取。',
    no_measurement: '專案尚無可供資料一覽顯示的量測紀錄。請確認量測資料已匯入。',
    no_instruments: '專案沒有儀器設定，因此無法建立資料一覽。請確認儀器資料已匯入。',
    all_instruments_closed: '專案的儀器皆已停用，因此沒有可顯示的儀器。',
    missing_measurement_batch: '最新量測資料找不到對應的批次紀錄。請檢查 Data 與 ListData 的 MeaNo 是否一致。',
    unsupported_instrument_types: '目前啟用的手動量測儀器類型無法供資料一覽使用（SID、SIS 或未定義的類型）。',
    no_measurement_rows: '此量測批次沒有讀值資料；儀器將顯示為無訊號。'
};

export function getOverviewDataMessage(data) {
    if (emptyReasons[data?.reasonCode]) return emptyReasons[data.reasonCode];
    const message = typeof data?.message === 'string' ? data.message.trim() : '';
    // 相容尚未更新的 WebApi。
    if (message === 'No measurement yet.') return emptyReasons.no_measurement;
    if (/^No Data rows found for MeaNo = \d+\.$/.test(message)) return emptyReasons.no_measurement_rows;
    return message || 'API 已完成回應，但沒有回傳任何儀器資料，且未提供原因。請確認專案的儀器設定及量測資料。';
}

export function getOverviewErrorMessage(error) {
    if (error?.status === 401) return '登入已失效或未通過驗證，請重新登入後再試。';
    if (error?.status === 403) return '目前帳號沒有讀取此專案資料的權限。';
    if (/failed to fetch|networkerror|load failed/i.test(error?.message || '')) {
        return '無法連線到資料服務。請確認 WebApi 是否啟動、網路連線與 HTTPS 憑證是否正常；瀏覽器也可能因跨來源設定而阻擋連線。';
    }
    return error?.message || '讀取資料時發生未預期的錯誤，請重新整理後再試。';
}

const rethrow = error => { throw error; };
const invalidResponse = (response, label) => new Error(
    typeof response?.message === 'string' && response.message.trim()
        ? response.message
        : `${label} API 回傳格式不正確。`);

// 輔助資料失敗不應使已取得的量測資料消失。
export async function loadDataOverview(api, token, projectId, bypassCache, selection) {
    const [status, gages, areas, tags] = await Promise.allSettled([
        selection === undefined
            ? api.siteData.getLatestDataWithAlert(token, projectId, 'zh-tw', rethrow, bypassCache)
            : api.siteData.getDataOverview(token, projectId, selection, bypassCache),
        api.siteData.getGageDefinition(token, [projectId], true, 'zh-tw', rethrow, bypassCache),
        api.siteData.getAreas(token, projectId, rethrow, bypassCache),
        api.instrumentTags.listByProjectId(token, projectId, rethrow)
    ]);
    if (status.status === 'rejected') throw status.reason;
    const history = selection === undefined ? null : status.value;
    const statusData = history ? history.data : status.value;
    const isRecord = item => item && typeof item === 'object' && !Array.isArray(item);
    if (!Array.isArray(statusData?.entries) || !statusData.entries.every(isRecord)) {
        throw invalidResponse(statusData || status.value, '量測資料');
    }

    const warnings = [];
    function auxiliary(result, label, isValid, fallback) {
        if (result.status === 'fulfilled' && isValid(result.value)) return result.value;
        const error = result.status === 'rejected' ? result.reason : invalidResponse(result.value, label);
        warnings.push(`${label}載入失敗：${getOverviewErrorMessage(error)}`);
        return fallback;
    }
    const gageList = auxiliary(gages, '儀器定義（單位及儀器篩選）',
        data => Array.isArray(data?.items?.[0]) && data.items[0].every(item => isRecord(item) && item.code != null),
        { items: [[]] }).items[0];
    const areaList = auxiliary(areas, '區域選單',
        data => Array.isArray(data?.items), { items: [] });
    const tagList = auxiliary(tags, 'GeoNow 標籤',
        data => data?.status === 1 && Array.isArray(data.message) && data.message.every(item =>
            isRecord(item) && item.id != null && (item.pointIndices == null || Array.isArray(item.pointIndices))),
        { message: [] }).message;
    const unitByCode = new Map(gageList.map(gage => [Number(gage.code), gage.unitValue || '']));
    const tagsByPoint = new Map();
    tagList.forEach(tag => (tag.pointIndices || []).forEach(pointIdx => {
        const pointTags = tagsByPoint.get(Number(pointIdx)) || [];
        pointTags.push(tag);
        tagsByPoint.set(Number(pointIdx), pointTags);
    }));
    return {
        history,
        data: { ...statusData, entries: statusData.entries.map(entry => {
            const pointTags = tagsByPoint.get(Number(entry.pointIdx)) || [];
            const names = pointTags.map(tag => tag.name).join('、');
            return { ...entry, unitValue: unitByCode.get(Number(entry.gageTypeCode)) || '',
                geoNowTags: pointTags, tagIds: pointTags.map(tag => tag.id), tagNames: names,
                tagGroup: names || '（無標籤）' };
        }) },
        gageList, areaList, tagList, warnings
    };
}
